// src/constants/brandingColors.js
//
// The curated 17-color palette organizers can choose from for their two
// branding slots (primary = organization name text color, accent =
// secondary buttons/highlights).
//
// IMPORTANT: this is the frontend's copy. The backend has its own copy
// (src/lib/brandingColors.js) for server-side validation -- there's no
// shared-package setup between the two repos, so these two files must be
// kept in sync by hand whenever the palette changes. If this becomes a
// real maintenance problem, consider an npm workspace or a small
// published shared package as the real fix -- not yet done.
//
// Every color here passes WCAG AA (>=4.5:1) as text directly on the
// app's dark page background (bg-gray-950) and as a button fill with
// black label text -- see the backend copy's header comment for the
// full rationale (this is also why Burgundy isn't here: it failed the
// as-text check, and why black/pure blue/brighter blue were rejected
// when Red/Green/Blue were added later for the same reason).
//
// Red/Green/Blue exist to give organizers with an existing brand
// identity (e.g. a school's literal colors) a genuinely "pure primary"
// option, even though each sits fairly close to an existing softer color
// (Crimson, Royal Blue, Emerald) by the distance check below -- expected,
// not a flaw; picking two similar ones together correctly triggers the
// "too similar" warning.

export const BRANDING_COLORS = [
    { name: 'Red',            hex: '#ff0000', labelColor: 'black' },
    { name: 'Crimson',        hex: '#e0473e', labelColor: 'black' },
    { name: 'Coral pink',     hex: '#ff5c8a', labelColor: 'black' },
    { name: 'Sunflower gold', hex: '#f2c14e', labelColor: 'black' },
    { name: 'Lime',           hex: '#a3e635', labelColor: 'black' },
    { name: 'Green',          hex: '#16a34a', labelColor: 'black' },
    { name: 'Emerald',        hex: '#2ecc71', labelColor: 'black' },
    { name: 'Mint',           hex: '#34d399', labelColor: 'black' },
    { name: 'Teal',           hex: '#14b8a6', labelColor: 'black' },
    { name: 'Cyan',           hex: '#22d3ee', labelColor: 'black' },
    { name: 'Sky blue',       hex: '#38bdf8', labelColor: 'black' },
    { name: 'Blue',           hex: '#3b82f6', labelColor: 'black' },
    { name: 'Royal blue',     hex: '#4f7cff', labelColor: 'black' },
    { name: 'Indigo',         hex: '#818cf8', labelColor: 'black' },
    { name: 'Violet',         hex: '#a855f7', labelColor: 'black' },
    { name: 'Magenta',        hex: '#e879f9', labelColor: 'black' },
    { name: 'Slate',          hex: '#94a3b8', labelColor: 'black' },
];

// Rough perceptual-distance check used to warn (not block) an organizer
// picking two colors that are technically different but visually very
// similar (e.g. Sky Blue + Royal Blue would both pass the hard
// primary !== accent rule but look like a mistake, not a choice).
// Simple Euclidean distance in RGB space -- not perceptually uniform,
// but plenty good enough for "are these two clearly different colors"
// at this palette's scale (17 hand-picked colors, a few deliberately
// close to each other -- see Red/Green/Blue note above).
function hexToRgb(hex) {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function colorsAreTooSimilar(hexA, hexB) {
    if (!hexA || !hexB) return false;
    const [r1, g1, b1] = hexToRgb(hexA);
    const [r2, g2, b2] = hexToRgb(hexB);
    const distance = Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
    // Max possible distance is ~441 (black to white). A threshold around
    // 100 catches "two shades of blue" while leaving genuinely distinct
    // pairs (e.g. Crimson + Royal Blue) alone.
    return distance < 100;
}